
const engine = require('./engine.js'); // Portions Copyright 2019 Jimb Esser (https://github.com/Jimbly/)
// Released under MIT License: https://opensource.org/licenses/MIT
// Some code from Turbulenz: Copyright (c) 2012-2013 Turbulenz Limited
// Released under MIT License: https://opensource.org/licenses/MIT

const geom = require('./geom.js');
const shaders = require('./shaders.js');
const textures = require('./textures.js');

const { vec2, vec3, vec4, v4set } = require('./vmath.js');

const shader_data = {
  vp_copy: {
    vp: '#pragma WebGL\nprecision highp float;\n\nvarying vec2 interp_texcoord;\nattribute vec2 POSITION;\n\nuniform vec2 copy_uv_scale;\nuniform vec4 clip_space;\n\nvoid main()\n{\n  interp_texcoord = POSITION * copy_uv_scale;\n  gl_Position = vec4(POSITION * clip_space.xy + clip_space.zw, 0, 1);\n}'
  },
  copy: {
    fp: '#pragma WebGL\n\nprecision highp float;\nprecision highp int;\n\nvarying vec2 interp_texcoord;\n\nuniform sampler2D inputTexture0;\nvoid main()\n{\n  gl_FragColor = texture2D(inputTexture0, interp_texcoord);\n}\n'
  },
  pixely_expand: {
    fp: '// Portions Copyright 2019 Jimb Esser (https://github.com/Jimbly/)\n// Released under MIT License: https://opensource.org/licenses/MIT\n#pragma WebGL\n\nprecision mediump float;\nprecision mediump int;\n\nvarying highp vec2 interp_texcoord;\nuniform sampler2D inputTexture0; // source\nuniform sampler2D inputTexture1; // hblur\nuniform sampler2D inputTexture2; // hblur+vblur\nuniform vec4 orig_pixel_size;\n\n// 1D Gaussian.\nfloat Gaus(float pos, float scale) {\n  return exp2(scale*pos*pos);\n}\n\nconst float SHADE = 0.75;\nconst float EASING = 1.25;\n\n#define DO_WARP\n#ifdef DO_WARP\n// Display warp.\n// 0.0 = none\n// 1.0/8.0 = extreme\nconst vec2 WARP=vec2(1.0/32.0,1.0/24.0);\n\n// Distortion of scanlines, and end of screen alpha.\nvec2 Warp(vec2 pos){\n  pos=pos*2.0-1.0;\n  pos*=vec2(1.0+(pos.y*pos.y)*WARP.x,1.0+(pos.x*pos.x)*WARP.y);\n  return pos*0.5+0.5;\n}\n#else\n#define Warp(v) v\n#endif\n\nfloat easeInOut(float v) {\n  float va = pow(v, EASING);\n  return va / (va + pow((1.0 - v), EASING));\n}\n\nfloat easeIn(float v) {\n  return 2.0 * easeInOut(0.5 * v);\n}\n\nfloat easeOut(float v) {\n  return 2.0 * easeInOut(0.5 + 0.5 * v) - 1.0;\n}\n\nvoid main()\n{\n  vec2 texcoords = Warp(interp_texcoord);\n  vec2 intcoords = (floor(texcoords.xy * orig_pixel_size.xy) + 0.5) * orig_pixel_size.zw;\n  vec2 deltacoords = (texcoords.xy - intcoords) * orig_pixel_size.xy; // -0.5 ... 0.5\n  // for horizontal sampling, map [-0.5 .. -A .. A .. 0.5] -> [-0.5 .. 0 .. 0 .. 0.5];\n  float A = 0.25;\n  float Ainv = (0.5 - A) * 2.0;\n  float uoffs = clamp((abs(deltacoords.x) - A) / Ainv, 0.0, 1.0) * orig_pixel_size.z;\n  uoffs *= sign(deltacoords.x);\n  vec2 sample_coords = vec2(intcoords.x + uoffs, intcoords.y);\n  // sample_coords = intcoords;\n  vec3 color = texture2D(inputTexture1, sample_coords).rgb;\n  vec3 color_scanline = texture2D(inputTexture2, texcoords.xy + vec2(0.0, 0.5 * orig_pixel_size.w)).rgb * SHADE;\n  // color_scanline = vec3(0);\n\n  // float mask = Gaus(deltacoords.y, -12.0);\n  float mask = easeOut(2.0*(0.5 - abs(deltacoords.y)));\n  // float mask = abs(deltacoords.y) > 0.25 ? 0.0 : 1.0;\n  color = mix(color_scanline, color, mask);\n  // color = vec3(mask);\n\n#ifdef DO_WARP\n  // vignette\n  float dist = min(1.0, 100.0 * min(0.5 - abs(texcoords.x - 0.5), 0.5 - abs(texcoords.y - 0.5)));\n  color *= 0.5 + 0.5 * dist;\n#endif\n\n  gl_FragColor = vec4(color, 1.0);\n  // gl_FragColor = vec4(color_scanline, 1.0);\n  // gl_FragColor = vec4(sample_coords, 0.0, 1.0);\n}\n'
  },
  gaussian_blur: {
    fp: '#pragma WebGL\n\nprecision highp float;\nprecision highp int;\n\nvarying vec2 interp_texcoord;\n\nvec4 _ret_0;\nvec4 _TMP2;\nvec4 _TMP1;\nvec2 _c0022;\nvec2 _c0024;\nuniform vec3 sampleRadius;\nuniform sampler2D inputTexture0;\n\nvoid main()\n{\n  vec2 uv = interp_texcoord;\n  vec2 step = sampleRadius.xy;\n  float glow = sampleRadius.z;\n  gl_FragColor =\n    ((texture2D(inputTexture0, uv - step * 3.0) + texture2D(inputTexture0, uv + step * 3.0)) * 0.085625 +\n    (texture2D(inputTexture0, uv - step * 2.0) + texture2D(inputTexture0, uv + step * 2.0)) * 0.12375 +\n    (texture2D(inputTexture0, uv - step * 1.0) + texture2D(inputTexture0, uv + step * 1.0)) * 0.234375 +\n    texture2D(inputTexture0, uv) * 0.3125) * 0.83333333333333333333333333333333 * glow;\n}\n'
  },
  // bloom_merge: {
  //   fp: fs.readFileSync(`${__dirname}/shaders/effects_bloom_merge.fp`, 'utf8'),
  // },
  // bloom_threshold: {
  //   fp: fs.readFileSync(`${__dirname}/shaders/effects_bloom_threshold.fp`, 'utf8'),
  // },
  color_matrix: {
    fp: '#pragma WebGL\n\nprecision highp float;\n\nvarying vec2 interp_texcoord;\n\nuniform vec4 colorMatrix[3];\nuniform sampler2D tex0;\n\nvoid main()\n{\n  vec4 _color;\n  vec4 _mutc;\n  _color = texture2D(tex0, interp_texcoord);\n  _mutc = _color;\n  _mutc.w = 1.0;\n  vec3 _r0019;\n  _r0019.x = dot(colorMatrix[0], _mutc);\n  _r0019.y = dot(colorMatrix[1], _mutc);\n  _r0019.z = dot(colorMatrix[2], _mutc);\n  _mutc.xyz = _r0019;\n  _mutc.w = _color.w;\n  gl_FragColor = _mutc;\n}'
  }
  // distort: {
  //   fp: fs.readFileSync(`${__dirname}/shaders/effects_distort.fp`, 'utf8'),
  // },
};

export function registerShader(key, obj) {
  shader_data[key] = obj;
}

function getShader(key) {
  let elem = shader_data[key];
  if (!elem.shader) {
    if (elem.fp) {
      elem.shader = shaders.create(gl.FRAGMENT_SHADER, elem.fp);
    } else {
      elem.shader = shaders.create(gl.VERTEX_SHADER, elem.vp);
    }
  }
  return elem.shader;
}

let inited = false;
let clip_space = vec4(2, 2, -1, -1);
let copy_uv_scale = vec2(1, 1);
let shader_params_default = {
  clip_space,
  copy_uv_scale
};
// let shader_params_distort;
let shader_params_color_matrix;
// let shader_params_bloom;
// let shader_params_bloom_merge;
let shader_params_gaussian_blur;
let shader_params_pixely_expand;
let quad_geom;
function startup() {
  inited = true;

  quad_geom = geom.create([[shaders.semantic.POSITION, gl.FLOAT, 2, false]], new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]), null, geom.QUADS); // TODO: use gl.TRIANGLE_STRIP instead, save index buffer binding

  // shader_params_distort = {
  //   clip_space,
  //   copy_uv_scale,
  //   strength: vec2(0, 0),
  //   transform: new Float32Array([0, 0, 0, 0, 0, 0]),
  //   invTransform: vec4(0, 0, 0, 0),
  // };

  shader_params_color_matrix = {
    clip_space,
    copy_uv_scale,
    colorMatrix: new Float32Array([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0])
  };

  // shader_params_bloom = {
  //   clip_space,
  //   copy_uv_scale,
  //   bloomThreshold: 0,
  //   thresholdCuttoff: 0,
  // };

  // shader_params_bloom_merge = {
  //   clip_space,
  //   copy_uv_scale,
  //   bloomIntensity: 0,
  //   bloomSaturation: 0,
  //   originalIntensity: 0,
  //   originalSaturation: 0,
  // };

  // Gaussian Blur effect (also used by bloom)
  shader_params_gaussian_blur = {
    clip_space,
    copy_uv_scale,
    sampleRadius: vec3(1, 1, 1),
    Gauss: new Float32Array([0.93, 0.8, 0.7, 0.6, 0.5, 0.4, 0.3, 0.2, 0.1])
  };

  shader_params_pixely_expand = {
    clip_space,
    copy_uv_scale,
    orig_pixel_size: vec4()
  };
}

export function grayScaleMatrix(dst) {
  dst[0] = 0.2126;
  dst[1] = 0.2126;
  dst[2] = 0.2126;
  dst[3] = 0.7152;
  dst[4] = 0.7152;
  dst[5] = 0.7152;
  dst[6] = 0.0722;
  dst[7] = 0.0722;
  dst[8] = 0.0722;
  dst[9] = dst[10] = dst[11] = 0;
}

export function sepiaMatrix(dst) {
  dst[0] = 0.393;
  dst[1] = 0.349;
  dst[2] = 0.272;
  dst[3] = 0.769;
  dst[4] = 0.686;
  dst[5] = 0.534;
  dst[6] = 0.189;
  dst[7] = 0.168;
  dst[8] = 0.131;
  dst[9] = dst[10] = dst[11] = 0;
}

export function negativeMatrix(dst) {
  dst[0] = dst[4] = dst[8] = -1;
  dst[1] = dst[2] = dst[3] = dst[5] = dst[6] = dst[7] = 0;
  dst[9] = dst[10] = dst[11] = 1;
}

export function saturationMatrix(dst, saturationScale) {
  let is = 1 - saturationScale;
  dst[0] = is * 0.2126 + saturationScale;
  dst[1] = is * 0.2126;
  dst[2] = is * 0.2126;
  dst[3] = is * 0.7152;
  dst[4] = is * 0.7152 + saturationScale;
  dst[5] = is * 0.7152;
  dst[6] = is * 0.0722;
  dst[7] = is * 0.0722;
  dst[8] = is * 0.0722 + saturationScale;
  dst[9] = dst[10] = dst[11] = 0;
}

export function hueMatrix(dst, angle) {
  ////
  //// Uncomment to calculate new coeffecients should luminance
  //// values 0.2126 0.7152 0.0722 change.
  //let lumR = 0.2126;
  //let lumG = 0.7152;
  //let lumB = 0.0722;
  ////
  //let r23 = Math.sqrt(2 / 3);
  //let r12 = 1 / Math.sqrt(2);
  //let r13 = 1 / Math.sqrt(3);
  //let r16 = 1 / Math.sqrt(6);
  //let M = [r23, 0, r13, -r16, r12, r13, -r16, -r12, r13, 0, 0, 0];
  //let zx = (r23 * lumR) - (r16 * lumG) - (r16 * lumB);
  //let zy =                (r12 * lumG) - (r12 * lumB);
  //let zz = (r13 * lumR) + (r13 * lumG) + (r13 * lumB);
  //let x = zx / zz;
  //let y = zy / zz;
  //let C = [1, 0, x, 0, 1, y, 0, 0, 1, 0, 0, 0];
  //m43mul(M, M, C);
  //console.log("Pre transform = ", M);
  //let E = [1, 0, -x, 0, 1, -y, 0, 0, 1, 0, 0, 0];
  //let N = [r23, -r16, -r16, 0, r12, -r12, r13, r13, r13, 0, 0, 0];
  //m43mul(N, E, N);
  //console.log("Post transform = ", N);
  ////
  //// Final matrix is then: m43Mul(Pre, [c, s, 0, -s, c, 0, 0, 0, 1, 0, 0, 0, ], Post);
  //// for c = cos(angle), s = sin(angle)
  ////
  //let out = "";
  //out += "let c = Math.cos(angle);\n";
  //out += "let s = Math.sin(angle);\n";
  //out += "dst[0] = (" + (N[0]*M[0]+N[3]*M[1]) + " * c) + (" + (N[3]*M[0]-N[0]*M[1]) + " * s) + " + lumR+";\n";
  //out += "dst[1] = (" + (-lumR)               + " * c) + (" + (N[4]*M[0]-N[1]*M[1]) + " * s) + " + lumR+";\n";
  //out += "dst[2] = (" + (-lumR)               + " * c) + (" + (N[5]*M[0]-N[2]*M[1]) + " * s) + " + lumR+";\n";
  //out += "dst[3] = (" + (-lumG)               + " * c) + (" + (N[3]*M[3]-N[0]*M[4]) + " * s) + " + lumG+";\n";
  //out += "dst[4] = (" + (N[1]*M[3]+N[4]*M[4]) + " * c) + (" + (N[4]*M[3]-N[1]*M[4]) + " * s) + " + lumG+";\n";
  //out += "dst[5] = (" + (-lumG)               + " * c) + (" + (N[5]*M[3]-N[2]*M[4]) + " * s) + " + lumG+";\n";
  //out += "dst[6] = (" + (-lumB)               + " * c) + (" + (N[3]*M[6]-N[0]*M[7]) + " * s) + " + lumB+";\n";
  //out += "dst[7] = (" + (-lumB)               + " * c) + (" + (N[4]*M[6]-N[1]*M[7]) + " * s) + " + lumB+";\n";
  //out += "dst[8] = (" + (N[2]*M[6]+N[5]*M[7]) + " * c) + (" + (N[5]*M[6]-N[2]*M[7]) + " * s) + " + lumB+";\n";
  //console.log(out);
  let c = Math.cos(angle);
  let s = Math.sin(angle);
  dst[0] = 0.7874 * c + -0.3712362230889293 * s + 0.2126;
  dst[1] = -0.2126 * c + 0.20611404610069642 * s + 0.2126;
  dst[2] = -0.2126 * c + -0.9485864922785551 * s + 0.2126;
  dst[3] = -0.7152 * c + -0.4962902913954023 * s + 0.7152;
  dst[4] = 0.2848 * c + 0.08105997779422341 * s + 0.7152;
  dst[5] = -0.7152 * c + 0.6584102469838492 * s + 0.7152;
  dst[6] = -0.0722 * c + 0.8675265144843316 * s + 0.0722;
  dst[7] = -0.0722 * c + -0.28717402389491986 * s + 0.0722;
  dst[8] = 0.9278 * c + 0.290176245294706 * s + 0.0722;
  dst[9] = dst[10] = dst[11] = 0;
}

export function brightnessAddMatrix(dst, brightnessOffset) {
  dst[0] = dst[4] = dst[8] = 1;
  dst[1] = dst[2] = dst[3] = dst[5] = dst[6] = dst[7] = 0;
  dst[9] = dst[10] = dst[11] = brightnessOffset;
}

export function brightnessScaleMatrix(dst, scale) {
  dst[0] = dst[4] = dst[8] = scale;
  dst[1] = dst[2] = dst[3] = dst[5] = dst[6] = dst[7] = 0;
  dst[9] = dst[10] = dst[11] = 0;
}

export function additiveMatrix(dst, additiveRGB) {
  dst[0] = dst[4] = dst[8] = 1;
  dst[1] = dst[2] = dst[3] = dst[5] = dst[6] = dst[7] = 0;
  dst[9] = additiveRGB[0];
  dst[10] = additiveRGB[1];
  dst[11] = additiveRGB[2];
}

export function contrastMatrix(dst, contrastScale) {
  dst[0] = dst[4] = dst[8] = contrastScale;
  dst[1] = dst[2] = dst[3] = dst[5] = dst[6] = dst[7] = 0;
  dst[9] = dst[10] = dst[11] = 0.5 * (1 - contrastScale);
}

// effect: { shader, params, texs }
function applyEffect(effect, view_w, view_h) {
  let viewport = engine.viewport;
  let target_w = viewport[2];
  let target_h = viewport[3];
  view_w = view_w || target_w;
  view_h = view_h || target_h;

  // Set values for parameters used by all effects
  clip_space[0] = 2.0 * view_w / target_w;
  clip_space[1] = 2.0 * view_h / target_h;
  // clip_space[2] = -1.0;
  // clip_space[3] = -1.0;
  // copy_uv_scale[0] = target_w / effect.coord_source.width;
  // copy_uv_scale[1] = target_h / effect.coord_source.height;

  shaders.bind(getShader('vp_copy'), getShader(effect.shader), effect.params);
  textures.bindArray(effect.texs);
  quad_geom.draw();
}

// // TODO: Update for RTBBCTT and port to new GLOV.js
// function applyBloomTODO(params) {
//   let source = params.source;
//   let blur1 = params.blurTarget1;
//   let blur2 = params.blurTarget2;
//   let dest = params.destination;
//   if (!source || !dest || !blur1 || !blur2 || !blur1.colorTexture0 ||
//     !blur2.colorTexture0 || blur1 === blur2 || blur1 === dest ||
//     source === blur1.colorTexture0 || source === dest.colorTexture0) {
//     return false;
//   }
//
//   let effectParams = this.effectParams;
//   let techparams;
//
//   // Threshold copy.
//   techparams = this.bloomThresholdParameters;
//   effectParams.technique = this.bloomThresholdTechnique;
//   effectParams.params = techparams;
//
//   techparams.bloomThreshold = (params.bloomThreshold !== undefined) ? params.bloomThreshold : 0.65;
//   techparams.thresholdCutoff = Math.exp((params.thresholdCutoff !== undefined) ? params.thresholdCutoff : 3);
//   techparams.inputTexture0 = source;
//   effectParams.destination = blur1;
//   this.applyEffect(effectParams);
//
//   // Gaussian blur.
//   techparams = this.gaussianBlurParameters;
//   effectParams.technique = this.gaussianBlurTechnique;
//   effectParams.params = techparams;
//
//   let sampleRadius = (params.blurRadius || 20);
//   techparams.sampleRadius[0] = sampleRadius / source.width;
//   techparams.sampleRadius[1] = 0;
//   techparams.sampleRadius[2] = 1;
//   techparams.inputTexture0 = blur1.colorTexture0;
//   effectParams.destination = blur2;
//   this.applyEffect(effectParams);
//
//   techparams.sampleRadius[0] = 0;
//   techparams.sampleRadius[1] = sampleRadius / source.height;
//   techparams.sampleRadius[2] = 1;
//   techparams.inputTexture0 = blur2.colorTexture0;
//   effectParams.destination = blur1;
//   this.applyEffect(effectParams);
//
//   // Merge.
//   techparams = this.bloomMergeParameters;
//   effectParams.technique = this.bloomMergeTechnique;
//   effectParams.params = techparams;
//
//   techparams.bloomIntensity = (params.bloomIntensity !== undefined) ? params.bloomIntensity : 1.2;
//   techparams.bloomSaturation = (params.bloomSaturation !== undefined) ? params.bloomSaturation : 1.2;
//   techparams.originalIntensity = (params.originalIntensity !== undefined) ? params.originalIntensity : 1.0;
//   techparams.originalSaturation = (params.originalSaturation !== undefined) ? params.originalSaturation : 1.0;
//   techparams.inputTexture0 = source;
//   techparams.inputTexture1 = blur1.colorTexture0;
//   effectParams.destination = dest;
//   this.applyEffect(effectParams);
//
//   return true;
// }

export function applyCopy(params) {
  if (!inited) {
    startup();
  }
  applyEffect({
    shader: params.shader || 'copy',
    params: shader_params_default,
    texs: [params.source]
  });
}

export function applyPixelyExpand(params) {
  if (!inited) {
    startup();
  }
  let source = params.source;
  source.setSamplerState({
    filter_min: gl.LINEAR,
    filter_mag: gl.LINEAR,
    wrap_s: gl.CLAMP_TO_EDGE,
    wrap_t: gl.CLAMP_TO_EDGE
  });

  // do horizontal blur for primary lines
  let resx = source.width;
  let resy = source.height;
  let sampleRadius = (params.hblur || 0.25) / resx;
  shader_params_gaussian_blur.sampleRadius[0] = sampleRadius;
  shader_params_gaussian_blur.sampleRadius[1] = 0;
  shader_params_gaussian_blur.sampleRadius[2] = 1;
  applyEffect({
    shader: 'gaussian_blur',
    params: shader_params_gaussian_blur,
    texs: [source]
  }, resx, resy);
  let hblur = engine.captureFramebuffer(null, resx, resy, true, false);

  // do seperable gaussian blur for scanlines (using horizontal blur from above)
  sampleRadius = (params.vblur || 0.75) / resy;
  shader_params_gaussian_blur.sampleRadius[0] = 0;
  shader_params_gaussian_blur.sampleRadius[1] = sampleRadius;
  shader_params_gaussian_blur.sampleRadius[2] = 1;
  applyEffect({
    shader: 'gaussian_blur',
    params: shader_params_gaussian_blur,
    texs: [hblur]
  }, resx, resy);
  let vblur = engine.captureFramebuffer(null, resx, resy, true, false);

  // combine at full res
  v4set(shader_params_pixely_expand.orig_pixel_size, source.width, source.height, 1 / source.width, 1 / source.height);

  engine.setViewport(params.final_viewport);
  if (params.clear_color) {
    gl.clearColor(params.clear_color[0], params.clear_color[1], params.clear_color[2], params.clear_color[3]);
    gl.clear(gl.COLOR_BUFFER_BIT);
  }

  applyEffect({
    shader: 'pixely_expand',
    params: shader_params_pixely_expand,
    texs: [source, hblur, vblur]
  });
}

export function applyGaussianBlur(params) {
  if (!inited) {
    startup();
  }
  let source = params.source;
  source.setSamplerState({
    filter_min: gl.LINEAR,
    filter_mag: gl.LINEAR,
    wrap_s: gl.CLAMP_TO_EDGE,
    wrap_t: gl.CLAMP_TO_EDGE
  });
  let max_size = params.max_size || 512;
  let min_size = params.min_size || 128;

  // Quick shrink down to 512->256->128 (or other specified min/max size)
  let inputTexture0 = source;

  let viewport = engine.viewport;
  let res = max_size;
  while (res > viewport[2] || res > viewport[3]) {
    res /= 2;
  }

  while (res > min_size) {
    applyEffect({
      shader: params.shader_copy || 'copy',
      params: shader_params_default,
      texs: [inputTexture0]
    }, res, res);
    inputTexture0 = engine.captureFramebuffer(null, res, res, true, false);
    res /= 2;
  }

  // Do seperable blur
  let sampleRadius = (params.blur || 1) / res;
  shader_params_gaussian_blur.sampleRadius[0] = sampleRadius;
  shader_params_gaussian_blur.sampleRadius[1] = 0;
  shader_params_gaussian_blur.sampleRadius[2] = params.glow || 1;
  applyEffect({
    shader: 'gaussian_blur',
    params: shader_params_gaussian_blur,
    texs: [inputTexture0]
  }, res, res);
  let blur = engine.captureFramebuffer(null, res, res, true, false);

  shader_params_gaussian_blur.sampleRadius[0] = 0;
  shader_params_gaussian_blur.sampleRadius[1] = sampleRadius;
  shader_params_gaussian_blur.sampleRadius[2] = params.glow || 1;
  applyEffect({
    shader: 'gaussian_blur',
    params: shader_params_gaussian_blur,
    texs: [blur]
  });

  return true;
}

export function applyColorMatrix(params) {
  if (!inited) {
    startup();
  }
  let source = params.source;
  source.setSamplerState({
    filter_min: gl.LINEAR,
    filter_mag: gl.LINEAR,
    wrap_s: gl.CLAMP_TO_EDGE,
    wrap_t: gl.CLAMP_TO_EDGE
  });

  let matrix = params.colorMatrix;
  let mout = shader_params_color_matrix.colorMatrix;

  mout[0] = matrix[0];
  mout[1] = matrix[3];
  mout[2] = matrix[6];
  mout[3] = matrix[9];
  mout[4] = matrix[1];
  mout[5] = matrix[4];
  mout[6] = matrix[7];
  mout[7] = matrix[10];
  mout[8] = matrix[2];
  mout[9] = matrix[5];
  mout[10] = matrix[8];
  mout[11] = matrix[11];

  applyEffect({
    shader: 'color_matrix',
    params: shader_params_color_matrix,
    texs: [source]
  });

  return true;
}

// // TODO: Update for RTBBCTT and port to new GLOV.js
// export function applyDistortTODO(params) {
//   let source = params.source;
//   let dest = params.destination;
//   let distort = params.distortion;
//   if (!source || !dest || !distort || !dest.colorTexture0 ||
//     source === dest.colorTexture0 || distort === dest.colorTexture0) {
//     return false;
//   }
//
//   // input transform.
//   //  a b tx
//   //  c d ty
//   let a;
//   let b;
//   let c;
//   let d;
//   let tx;
//   let ty;
//
//   let transform = params.transform;
//   if (transform) {
//     // transform col-major.
//     a = transform[0];
//     b = transform[2];
//     tx = transform[4];
//     c = transform[1];
//     d = transform[3];
//     ty = transform[5];
//   } else {
//     a = d = 1;
//     b = c = 0;
//     tx = ty = 0;
//   }
//
//   let effectParams = this.effectParams;
//   let techparams = this.distortParameters;
//   effectParams.technique = this.distortTechnique;
//   effectParams.params = techparams;
//
//   // TODO: Cache 'transform', 'invTransform', etc in the code below
//   techparams.transform[0] = a;
//   techparams.transform[1] = b;
//   techparams.transform[2] = tx;
//   techparams.transform[3] = c;
//   techparams.transform[4] = d;
//   techparams.transform[5] = ty;
//
//   // Compute inverse transform to use in distort texture displacement..
//   let idet = 1 / (a * d - b * c);
//   let ia = techparams.invTransform[0] = (idet * d);
//   let ib = techparams.invTransform[1] = (idet * -b);
//   let ic = techparams.invTransform[2] = (idet * -c);
//   let id = techparams.invTransform[3] = (idet * a);
//
//   // Compute max pixel offset after transform for normalisation.
//   let x1 = ((ia + ib) * (ia + ib)) + ((ic + id) * (ic + id));
//   let x2 = ((ia - ib) * (ia - ib)) + ((ic - id) * (ic - id));
//   let x3 = ((-ia + ib) * (-ia + ib)) + ((-ic + id) * (-ic + id));
//   let x4 = ((-ia - ib) * (-ia - ib)) + ((-ic - id) * (-ic - id));
//   let xmax = 0.5 * Math.sqrt(Math.max(x1, x2, x3, x4));
//
//   let strength = (params.strength || 10);
//   techparams.strength[0] = strength / (source.width * xmax);
//   techparams.strength[1] = strength / (source.height * xmax);
//
//   techparams.inputTexture0 = source;
//   techparams.distortTexture = distort; // linear / repeat
//   effectParams.destination = dest;
//   this.applyEffect(effectParams);
//
//   return true;
// }